import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { formatDateDDMMYY } from '../../../components/common/dateUtils';
import NoRecordFound from '../../../components/common/no-record-found';



const A4PrintPreview = ({ insuranceData, onBack, autoPrint = false }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState(insuranceData);
  const [isReadyToPrint, setIsReadyToPrint] = useState(false);

  useEffect(() => {
    // Add print styles
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        @page {
          size: A4 portrait;
          margin: 10mm;
        }
        body {
          background: #fff !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .a4-print-container {
          width: 210mm !important;
          min-height: 297mm !important;
          margin: 0 auto !important;
          box-shadow: none !important;
          background: #fff !important;
          padding: 15px !important;
          page-break-inside: avoid !important;
        }
        .no-print {
          display: none !important;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
      }
    `;
    document.head.appendChild(style);

    // Mark as ready after styles are applied
    setTimeout(() => {
      setIsReadyToPrint(true);
    }, 200);

    // Cleanup function
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Auto print when component is ready and autoPrint is enabled
  useEffect(() => {
    if (autoPrint && isReadyToPrint) {
      setTimeout(() => {
        window.print();
      }, 300);
    }
  }, [autoPrint, isReadyToPrint]);

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      // Helper function to format date for database (YYYY-MM-DD)
      const formatDateForDB = (dateValue) => {
        if (!dateValue) return null;
        
        // If it's already in YYYY-MM-DD format
        if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
          return dateValue;
        }
        
        // If it's in ISO format or other date format
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return null;
        
        return date.toISOString().split('T')[0];
      };

      // Only include fields that exist in the database table
      const updateData = {
        buyer_id: editedData.buyer_id,
        supplier_id: editedData.supplier_id,
        contract_no: editedData.contract_no,
        commodity_id: editedData.commodity_id,
        order_id: editedData.order_id,
        quantity: editedData.quantity,
        rate: editedData.rate,
        amount_usd: editedData.amount_usd,
        amount_inr: editedData.amount_inr,
        usd_inr_product: editedData.usd_inr_product,
        markup_percent: editedData.markup_percent,
        markup: editedData.markup,
        total_value: editedData.total_value,
        insurance_premium: editedData.insurance_premium,
        gst: editedData.gst,
        total_premium: editedData.total_premium,
        gst_no: editedData.gst_no,
        policy_number: editedData.policy_number,
        add_on_amount: editedData.add_on_amount,
        type: editedData.type,
        departure_date: formatDateForDB(editedData.departure_date),
        arrival_date: formatDateForDB(editedData.arrival_date),
        vessel_name: editedData.vessel_name,
        eta_port: editedData.eta_port
      };

      const response = await fetch(`${API_BASE_URL}/api/insurance/marine-insurance/${editedData.insurance_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const result = await response.json();
        alert('Insurance data updated successfully!');
        setIsEditMode(false);
        // Update the local data with the response
        setEditedData(result.data);
        // Refresh the data
        window.location.reload();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update insurance data');
      }
    } catch (error) {
      console.error('Error updating insurance data:', error);
      alert('Error updating insurance data: ' + error.message);
    }
  };

  const handleCancel = () => {
    setEditedData(insuranceData);
    setIsEditMode(false);
  };

  const renderEditableField = (field, value, type = 'text') => {
    if (!isEditMode) {
      if (type === 'date') {
        return formatDateDDMMYY(value) || '';
      }
      return value || '';
    }

    if (type === 'textarea') {
      return (
        <textarea
          value={value || ''}
          onChange={(e) => handleInputChange(field, e.target.value)}
          style={{
            width: '100%',
            minHeight: '60px',
            padding: '8px',
            border: '1px solid #cbd5e1',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'inherit'
          }}
        />
      );
    }

    if (type === 'date') {
      // Format date for HTML date input (YYYY-MM-DD)
      const formatDateForInput = (dateValue) => {
        if (!dateValue) return '';
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      };

      return (
        <input
          type="date"
          value={formatDateForInput(value)}
          onChange={(e) => handleInputChange(field, e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #cbd5e1',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'inherit'
          }}
        />
      );
    }

    return (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => handleInputChange(field, e.target.value)}
        style={{
          width: '100%',
          padding: '8px',
          border: '1px solid #cbd5e1',
          borderRadius: '4px',
          fontSize: '14px',
          fontFamily: 'inherit'
        }}
      />
    );
  };

  return (
    <div style={{
      background: '#f8fafc',
      minHeight: '100vh',
      padding: '10px 0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }} className="sm:p-5">
      <div
        className="a4-print-container w-full max-w-4xl mx-auto"
        style={{
          background: '#fff',
          width: '900px', // This will be overridden by print CSS
          minHeight: '1123px',
          padding: '15px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 32 }} className="no-print">
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }} className="flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            {isEditMode ? (
              <>
                <button onClick={handleSave} style={{ 
                  padding: '8px 20px', 
                  background: '#10b981', 
                  color: '#fff', 
                  border: '1px solid #10b981', 
                  borderRadius: 4, 
                  fontWeight: 600, 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }} onMouseOver={e => e.currentTarget.style.background = '#059669'} onMouseOut={e => e.currentTarget.style.background = '#10b981'}>Save</button>
                <button onClick={handleCancel} style={{ 
                  padding: '8px 20px', 
                  background: '#ef4444', 
                  color: '#fff', 
                  border: '1px solid #ef4444', 
                  borderRadius: 4, 
                  fontWeight: 600, 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }} onMouseOver={e => e.currentTarget.style.background = '#dc2626'} onMouseOut={e => e.currentTarget.style.background = '#ef4444'}>Cancel</button>
              </>
            ) : (
              <button onClick={() => setIsEditMode(true)} style={{ 
                padding: '8px 20px', 
                background: '#2691CC', 
                color: '#fff', 
                border: '1px solid #2691CC', 
                borderRadius: 4, 
                fontWeight: 600, 
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }} onMouseOver={e => e.currentTarget.style.background = '#1f7aad'} onMouseOut={e => e.currentTarget.style.background = '#2691CC'}>Edit</button>
            )}
            <button onClick={onBack} style={{ 
              marginRight: 16, 
              padding: '8px 20px', 
              background: '#2691CC', 
              color: '#fff', 
              border: '1px solid #2691CC', 
              borderRadius: 4, 
              fontWeight: 600, 
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              width: '100%'
            }} className="sm:w-auto" onMouseOver={e => e.currentTarget.style.background = '#1f7aad'} onMouseOut={e => e.currentTarget.style.background = '#2691CC'}>Back to List</button>
            <button onClick={() => window.print()} disabled={!isReadyToPrint} style={{
              padding: '8px 20px',
              background: isReadyToPrint ? '#2691CC' : '#9CA3AF',
              color: '#fff',
              border: `1px solid ${isReadyToPrint ? '#2691CC' : '#9CA3AF'}`,
              borderRadius: 4,
              fontWeight: 600,
              cursor: isReadyToPrint ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease'
            }} onMouseOver={e => {
              if (isReadyToPrint) e.currentTarget.style.background = '#1f7aad';
            }} onMouseOut={e => {
              if (isReadyToPrint) e.currentTarget.style.background = '#2691CC';
            }}>
              {isReadyToPrint ? 'Print' : 'Loading...'}
            </button>
          </div>
        </div>
        <h2 style={{ 
          textAlign: 'center', 
          marginBottom: 16, 
          fontSize: 24, 
          fontWeight: 700, 
          letterSpacing: 1,
          color: '#1e3a8a'
        }}>Marine Insurance </h2>
        
        <div style={{ 
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          overflow: 'hidden',
          marginBottom: 16
        }}>
                      <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: 14
            }}>
            <tbody>
            
              <tr>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '8px 12px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Buyer</td>
                <td style={{ 
                  padding: '8px 12px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{editedData.buyer_name}</td>
              </tr>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '8px 12px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Seller</td>
                <td style={{ 
                  padding: '8px 12px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{editedData.supplier_name}</td>
              </tr>
              <tr>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '8px 12px',
                  borderBottom: '1px solid #e2e8f0'
                }}>State of Purchase</td>
                <td style={{ 
                  padding: '8px 12px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{editedData.buyer_state}</td>
              </tr>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Address</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{editedData.buyer_address}</td>
              </tr>
              <tr>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Departure Date</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('departure_date', editedData.departure_date, 'date')}</td>
              </tr>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Arrival Date</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('arrival_date', editedData.arrival_date, 'date')}</td>
              </tr>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Loading Port</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('loading_port', editedData.loading_port)}</td>
              </tr>
              <tr>
                <td style={{
                  fontWeight: 600,
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Destination Port</td>
                <td style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('destination_port', editedData.destination_port)}</td>
              </tr>
              <tr>
                <td style={{
                  fontWeight: 600,
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Vessel Name</td>
                <td style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('vessel_name', editedData.vessel_name)}</td>
              </tr>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <td style={{
                  fontWeight: 600,
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Purchase Contract</td>
                <td style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('contract_no', editedData.contract_no)}</td>
              </tr>
              <tr>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Commodity</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{editedData.commodity_name}</td>
              </tr>
              <tr>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Total Qty (MT)</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('quantity', editedData.quantity, 'number')}</td>
              </tr>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Unit Price (USD)</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('rate', editedData.rate, 'number')}</td>
              </tr>

              <tr>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Quantity (MT)</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('quantity', editedData.quantity, 'number')}</td>
              </tr>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Rate</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('rate', editedData.rate, 'number')}</td>
              </tr>
              <tr>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Amount US $</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('amount_usd', editedData.amount_usd, 'number')}</td>
              </tr>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Amount INR (Approx Rate)</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('amount_inr', editedData.amount_inr, 'number')}</td>
              </tr>
              <tr>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>USD × INR (Product)</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('usd_inr_product', editedData.usd_inr_product, 'number')}</td>
              </tr>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Mark up/Expenses @ (%)</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('markup_percent', editedData.markup_percent, 'number')}</td>
              </tr>
              <tr>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Mark up/Expenses Value</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>{renderEditableField('markup', editedData.markup, 'number')}</td>
              </tr>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <td style={{ 
                  fontWeight: 600, 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0'
                }}>Total Value</td>
                <td style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0',
                  fontWeight: 600,
                  color: '#1e40af'
                }}>{renderEditableField('total_value', editedData.total_value, 'number')}</td>
              </tr>
              <tr>
                <td style={{
                  fontWeight: 600,
                  padding: '12px 16px'
                }}>GST No</td>
                <td style={{
                  padding: '12px 16px'
                }}>{renderEditableField('gst_no', editedData.gst_no)}</td>
              </tr>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <td style={{
                  fontWeight: 600,
                  padding: '12px 16px'
                }}>Policy Number</td>
                <td style={{
                  padding: '12px 16px'
                }}>{renderEditableField('policy_number', editedData.policy_number)}</td>
              </tr>
              <tr>
                <td style={{
                  fontWeight: 600,
                  padding: '12px 16px'
                }}>Add-on Amount</td>
                <td style={{
                  padding: '12px 16px'
                }}>₹{renderEditableField('add_on_amount', editedData.add_on_amount, 'number')}</td>
              </tr>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <td style={{
                  fontWeight: 600,
                  padding: '12px 16px'
                }}>Add-on Type</td>
                <td style={{
                  padding: '12px 16px'
                }}>{renderEditableField('type', editedData.type)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Insurance Premium Calculation */}
        {editedData.insurance_premium && (
          <div style={{ 
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#f0f9ff'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 600,
              marginBottom: '10px',
              color: '#0369a1'
            }}>Insurance Premium Calculation</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span>Premium Total Value:</span>
              <span style={{ fontWeight: 500 }}>₹{renderEditableField('insurance_premium', editedData.insurance_premium, 'number')}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span>Policy Number:</span>
              <span style={{ fontWeight: 500 }}>{renderEditableField('policy_number', editedData.policy_number) || 'To be assigned'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span>Add-on Amount:</span>
              <span style={{ fontWeight: 500 }}>₹{renderEditableField('add_on_amount', editedData.add_on_amount, 'number')}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span>GST (18%):</span>
              <span style={{ fontWeight: 500 }}>₹{renderEditableField('gst', editedData.gst, 'number')}</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '12px',
              paddingTop: '8px',
              borderTop: '1px dashed #cbd5e1'
            }}>
              <span style={{ fontWeight: 600 }}>Total Premium Payable:</span>
              <span style={{ fontWeight: 600, color: '#1e40af' }}>₹{renderEditableField('total_premium', editedData.total_premium, 'number')}</span>
            </div>
            <div style={{
              marginTop: '12px',
              paddingTop: '8px',
              borderTop: '1px solid #cbd5e1'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 500 }}>Add-on Type:</span>
                <span style={{ fontWeight: 500, color: '#0369a1' }}>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={editedData.type || ''}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                      placeholder="Enter add-on type"
                      style={{
                        width: '120px',
                        padding: '4px 8px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontFamily: 'inherit'
                      }}
                    />
                  ) : (
                    editedData.type || ''
                  )}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Calculation Breakdown */}
        <div style={{ 
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#f8fafc'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: 600,
            marginBottom: '8px',
            color: '#374151'
          }}>Calculation Breakdown</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '14px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Quantity:</span>
                <span style={{ fontWeight: 500 }}>{editedData.quantity || 0} MT</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Rate:</span>
                <span style={{ fontWeight: 500 }}>${editedData.rate || 0}/MT</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Amount USD:</span>
                <span style={{ fontWeight: 500 }}>${editedData.amount_usd || 0}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Amount INR:</span>
                <span style={{ fontWeight: 500 }}>₹{editedData.amount_inr || 0}</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>USD × INR Product:</span>
                <span style={{ fontWeight: 500 }}>₹{editedData.usd_inr_product || 0}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Markup %:</span>
                <span style={{ fontWeight: 500 }}>{editedData.markup_percent || 0}%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Markup Value:</span>
                <span style={{ fontWeight: 500 }}>₹{editedData.markup || 0}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, color: '#1e40af' }}>
                <span>Total Value:</span>
                <span>₹{editedData.total_value || 0}</span>
              </div>
            </div>
          </div>
          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              <strong>Formula:</strong> Total Value = (Amount USD × Amount INR) + Markup Value
            </div>
          </div>
        </div>
        
        <div style={{ 
          textAlign: 'right', 
          marginTop: 16,
          paddingTop: '16px',
          borderTop: '1px solid #e2e8f0'
        }}>
          <div style={{ fontWeight: 600, fontSize: 16, marginBottom: '12px' }}>
            <div>For KRIBHCO</div>
            <div style={{ marginTop: '12px' }}>
              <div style={{ borderTop: '1px solid #000', width: '200px', display: 'inline-block' }}></div>
              <div style={{ marginTop: '4px' }}>Authorized Signatory</div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

const MarineInsuranceList = () => {
  const navigate = useNavigate();
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isAutoPrint, setIsAutoPrint] = useState(false);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchInsuranceData = async (page = 1, search = '', pendingOnly = false) => {
    setLoading(true);
    setError(null);
    try {
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        search: search
      });
      
      // Add filter parameter for pending policy numbers
      if (pendingOnly) {
        queryParams.append('filter', 'no_policy');
      }
      
      const response = await fetch(
        `${API_BASE_URL}/api/insurance/marine-insurance?${queryParams.toString()}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch insurance data');
      }
      
      const data = await response.json();

      // Remove duplicates based on insurance_id
      const uniqueData = data.data ? data.data.filter((item, index, self) =>
        index === self.findIndex(t => t.insurance_id === item.insurance_id)
      ) : [];

      setInsuranceData(uniqueData);
      setTotalPages(data.totalPages || 0);
      setTotalRecords(data.total || 0);
    } catch (err) {
      setError('Failed to load data');
      console.error('Error fetching insurance data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsuranceData(currentPage, debouncedSearch, showPendingOnly);
  }, [currentPage, debouncedSearch, showPendingOnly]);

  const handleFilterChange = (field, value) => {
    if (field === 'showPendingOnly') {
      setShowPendingOnly(value);
      setCurrentPage(1);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setShowPendingOnly(false);
    setCurrentPage(1);
  };

  const handleViewDetails = (insurance) => {
    setSelectedInsurance(insurance);
    setIsAutoPrint(false);
    setShowPreview(true);
  };

  const handlePrint = (insurance) => {
    setSelectedInsurance(insurance);
    setIsAutoPrint(true);
    setShowPreview(true);
  };

  if (showPreview && selectedInsurance) {
    return <A4PrintPreview
      insuranceData={selectedInsurance}
      onBack={() => {
        setShowPreview(false);
        setIsAutoPrint(false);
      }}
      autoPrint={isAutoPrint}
    />;
  }

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Marine Insurance</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Quick Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className={`flex items-center gap-1 px-3 py-1 text-xs rounded font-medium border ${
              showFilters 
                ? 'bg-gray-600 text-white border-gray-700 hover:bg-gray-700' 
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
            }`}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            onClick={() => navigate('/import/insurance')}
            className="flex items-center gap-1 bg-[#3899cf] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add New
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showPendingOnly}
                onChange={(e) => handleFilterChange('showPendingOnly', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">View No Policy Records (records without policy numbers)</span>
            </label>
          </div>
          <div className="mt-2 flex justify-end">
            <button
              onClick={resetFilters}
              className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 font-medium"
            >
              Reset All Filters
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : insuranceData.length === 0 ? (
        <NoRecordFound message="No marine insurance records found." />
      ) : (
        <>
          <div className="overflow-x-auto rounded border border-gray-50">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Contract No</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Buyer</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Supplier</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Commodity</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Vessel</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Total Value</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Premium</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Policy</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Add-on</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Type</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {insuranceData.map((insurance, i) => (
                  <tr key={insurance.insurance_id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-1 font-semibold whitespace-nowrap">{insurance.contract_no}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{insurance.buyer_name}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{insurance.supplier_name}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{insurance.commodity_name}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{insurance.vessel_name || '-'}</td>
                    <td className="px-3 py-1 whitespace-nowrap">₹{parseFloat(insurance.total_value || 0).toLocaleString()}</td>
                    <td className="px-3 py-1 whitespace-nowrap text-green-600">₹{parseFloat(insurance.total_premium || 0).toLocaleString()}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{insurance.policy_number || '-'}</td>
                    <td className="px-3 py-1 whitespace-nowrap">₹{parseFloat(insurance.add_on_amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{insurance.type || '-'}</td>
                    <td className="px-3 py-1 whitespace-nowrap">
                      <button
                        onClick={() => handleViewDetails(insurance)}
                        className="bg-[#3899cf] hover:bg-[#0e8dc6] text-white px-2 py-1 rounded text-xs mr-2"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handlePrint(insurance)}
                        className="bg-[#3899cf] hover:bg-[#0e8dc6] text-white px-2 py-1 rounded text-xs"
                      >
                        Print
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-end items-center gap-2 mt-3 text-xs">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`px-2 py-1 rounded border transition-colors duration-100 ${currentPage === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
              >
                Prev
              </button>
              <span className="text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`px-2 py-1 rounded border transition-colors duration-100 ${currentPage === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MarineInsuranceList;